'use client';

/**
 * useCookieConsent — H3 (Audit) — EU ePrivacy / DSGVO cookie-consent hook.
 *
 * Reads the visitor's cookie-consent state from `document.cookie`
 * (`zenopus_cc_v1=…`) and exposes mutators that POST the choices back to
 * `/api/cookie-consent`. The first response from the server has not yet
 * arrived when SSR renders the layout, so the source-of-truth on the
 * client is always the cookie — the API call is a fire-and-forget
 * persistence write so the choices survive cookie loss (e.g. after the
 * user clears site data on a different device).
 *
 * The four buckets are the canonical EU cookie-consent categories:
 *
 *   - `necessary`    — always true, cannot be turned off (session cookie,
 *                      CSRF token, auth, locale).
 *   - `analytics`    — Sentry, OpenObserve, product analytics. Off by default.
 *   - `marketing`    — Stripe checkout (post-funnel), referral attribution.
 *   - `performance`  — Web-Vitals, error rate sampling.
 *
 * The cookie itself is base64-encoded JSON so a future v2 schema can be
 * detected via the `_v` field; for v1 we only read/write `choices` and
 * `sessionId`.
 *
 * Memory rule (design-system-reuse): we deliberately do NOT pull in
 * `react-cookie-consent` — the entire mechanism is ~120 lines and the
 * library would drag in its own theming + i18n layer that fights ours.
 */

import { useCallback, useState, useSyncExternalStore } from 'react';
import { fetchWithAuth } from '@/lib/utils/api-auth';

export type CookieConsentChoices = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  performance: boolean;
};

const COOKIE_NAME = 'zenopus_cc_v1';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
const SESSION_ID_BYTES = 18; // 144 bits — random opaque session id.

type StoredConsent = {
  _v: 1;
  sessionId: string;
  choices: CookieConsentChoices;
  grantedAt: string;
};

/** Random URL-safe id used to correlate anonymous + post-login consent rows. */
function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint8Array(SESSION_ID_BYTES);
    crypto.getRandomValues(buf);
    let out = '';
    for (let i = 0; i < buf.length; i += 1) {
      out += buf[i].toString(16).padStart(2, '0');
    }
    return out;
  }
  // Fallback (non-secure) — only reached in environments without WebCrypto,
  // i.e. very old browsers or test stubs. Still good enough to dedupe rows
  // because the server treats the session id as opaque.
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

function readRawCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return null;
}

function writeRawCookie(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === 'undefined') return;
  // SameSite=Lax mirrors the auth-session cookie so a future cross-site
  // POST cannot set or clear consent. `path=/` makes the cookie visible
  // on every locale-prefixed route. We deliberately do NOT add Secure
  // here so local dev (http://localhost) keeps working; in production
  // the `secure` proxy upstream upgrades it via the `__Secure-` prefix
  // logic on the auth path.
  document.cookie = `${name}=${value};path=/;max-age=${maxAgeSeconds};SameSite=Lax`;
}

function parseStored(raw: string | null): StoredConsent | null {
  if (!raw) return null;
  try {
    // The cookie value is URL-component encoded so the JSON survives the
    // Set-Cookie roundtrip (commas/semicolons would otherwise terminate it).
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded) as Partial<StoredConsent>;
    if (parsed && parsed._v === 1 && parsed.choices && typeof parsed.sessionId === 'string') {
      // Coerce shape so a hand-edited cookie cannot poison downstream state.
      const choices: CookieConsentChoices = {
        necessary: true,
        analytics: Boolean(parsed.choices.analytics),
        marketing: Boolean(parsed.choices.marketing),
        performance: Boolean(parsed.choices.performance),
      };
      return {
        _v: 1,
        sessionId: parsed.sessionId,
        choices,
        grantedAt: typeof parsed.grantedAt === 'string' ? parsed.grantedAt : new Date().toISOString(),
      };
    }
  } catch {
    // Fall through — corrupt cookies are treated as "no consent yet" so
    // the banner re-prompts.
  }
  return null;
}

function serialiseStored(value: StoredConsent): string {
  return encodeURIComponent(JSON.stringify(value));
}

export type UseCookieConsentResult = {
  /** `null` until the user has made an explicit choice (banner is visible). */
  choices: CookieConsentChoices | null;
  /** Stable opaque session id; safe to send to the backend. */
  sessionId: string | null;
  /** Accept all four buckets. */
  accept: () => void;
  /** Reject all non-necessary buckets. */
  reject: () => void;
  /** Persist a custom selection from the granular dialog. */
  customize: (next: Omit<CookieConsentChoices, 'necessary'>) => void;
  /** True only after the first client render so SSR renders nothing. */
  hydrated: boolean;
};

/**
 * Read consent on mount, then keep it in component state so re-renders
 * after a button click do not have to re-parse the cookie. Persistence is
 * fire-and-forget — the cookie is the source of truth and the API call
 * is best-effort.
 */
/**
 * Module-level revision counter — bumped on every cookie write so that
 * subscribers re-read the cookie on the next React render. A plain
 * counter is enough because the actual value lives in `document.cookie`
 * (the source of truth); we only need to nudge React.
 */
let cookieRevision = 0;
const cookieListeners = new Set<() => void>();

function notifyCookieChanged() {
  cookieRevision += 1;
  for (const listener of cookieListeners) {
    listener();
  }
}

function subscribeToCookieRevision(callback: () => void) {
  cookieListeners.add(callback);
  return () => {
    cookieListeners.delete(callback);
  };
}

/** Stable serialised snapshot — `useSyncExternalStore` re-reads when this changes. */
function getCookieSnapshot(): string {
  return `${cookieRevision}|${readRawCookie(COOKIE_NAME) ?? ''}`;
}

/** SSR snapshot — banner stays unmounted on the server. */
function getCookieServerSnapshot(): string {
  return '0|';
}

export function useCookieConsent(): UseCookieConsentResult {
  // useSyncExternalStore reads the cookie without going through a
  // useEffect+setState pair, so the `react-hooks/set-state-in-effect`
  // rule stays happy. The snapshot string ensures React only re-renders
  // when the cookie genuinely changes.
  const snapshot = useSyncExternalStore(subscribeToCookieRevision, getCookieSnapshot, getCookieServerSnapshot);

  // Parse the snapshot lazily on each render. `parseStored` is cheap
  // (one JSON.parse on a tiny payload) and re-running it ensures we
  // reflect any external rewrites of `document.cookie`.
  const stored = parseStored(snapshot.slice(snapshot.indexOf('|') + 1) || null);

  // `hydrated` mirrors the SSR/CSR boundary. useSyncExternalStore returns
  // the SERVER snapshot during BOTH the SSR pass AND the client's first
  // (hydration) render, then re-renders with the real client snapshot.
  // The previous version OR-ed in `typeof document !== 'undefined'`,
  // which made hydrated=true on the first client render and produced a
  // SSR/CSR hydration mismatch (banner missing on server, banner present
  // on client). Keep the check on the snapshot alone — that's the
  // canonical pattern for useSyncExternalStore with a server snapshot.
  const hydrated = snapshot !== '0|';

  // Local "draft session id" so a fresh banner render that has never
  // written the cookie still has a stable id to send to the server.
  // We pin this once per hook instance and reuse the cookie's id as
  // soon as the user makes a choice.
  const [draftSessionId] = useState<string>(() => generateSessionId());

  const persist = useCallback(
    (choices: CookieConsentChoices) => {
      const next: StoredConsent = {
        _v: 1,
        sessionId: stored?.sessionId ?? draftSessionId,
        choices,
        grantedAt: new Date().toISOString(),
      };
      writeRawCookie(COOKIE_NAME, serialiseStored(next), ONE_YEAR_SECONDS);
      notifyCookieChanged();
      // Fire the DSGVO gate event — instrumentation-client.ts listens
      // for it and lights up Sentry + Web-Vitals lazily on accept-
      // after-load. The detail payload mirrors the cookie so other
      // listeners (analytics scripts, etc.) can skip a cookie re-read.
      if (typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('zenopus:cookieconsent:changed', { detail: choices }));
        } catch {
          // CustomEvent constructor missing on ancient browsers — no-op.
        }
      }

      // Best-effort backend write — explicitly silent so a failed request
      // never blocks the banner from disappearing. The cookie remains the
      // visible source of truth.
      void fetchWithAuth('/api/cookie-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: next.sessionId,
          choices: next.choices,
        }),
        silent: true,
      }).catch(() => {
        // Intentionally swallowed — see comment above.
      });
    },
    [stored, draftSessionId],
  );

  const accept = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true, performance: true });
  }, [persist]);

  const reject = useCallback(() => {
    persist({ necessary: true, analytics: false, marketing: false, performance: false });
  }, [persist]);

  const customize = useCallback(
    (next: Omit<CookieConsentChoices, 'necessary'>) => {
      persist({
        necessary: true,
        analytics: Boolean(next.analytics),
        marketing: Boolean(next.marketing),
        performance: Boolean(next.performance),
      });
    },
    [persist],
  );

  return {
    choices: stored?.choices ?? null,
    sessionId: stored?.sessionId ?? draftSessionId,
    accept,
    reject,
    customize,
    hydrated,
  };
}
